/**
 * GoodsReceiptPage 進貨 (S5.1) — Board D #5, Procurement nav (Board E pink
 * placement — NOT inventory; that section is dead vocabulary).
 *
 * Rebuilt out of the codex `shipping/ShippingPages.tsx` onto the house list
 * stack (useListPage + GenericFilterPopover + TableContainer state triad).
 *
 * - The docket modal prefills from the A3 裝櫃實際 actual-at-loading feed
 *   (`GET /goods-receipts/prefill`): sealed container numbers + the
 *   seal-fixed loaded quantities per PO line.
 * - `sealedConfirmed` 已封櫃 is a PRECONDITION: an unsealed PO shows the
 *   blocked explanation and the post action is HIDDEN (hide-don't-disable).
 * - Posting advances qtyReceived only — the load balance (待出量) is fixed at
 *   the seal and never rewritten; the server enforces both rules (409
 *   NOT_SEALED / OVER_RECEIPT) and the page localizes them.
 * - 會計對帳 lives in `finance/ReceiptInvoiceMatchView` — the toolbar links
 *   there; this page renders the per-docket match chip.
 *
 * Slice-local seam (S5.1 file-set): the table hook + api calls are co-located
 * here because `src/hooks/**` / `src/services/api/**` / `src/stores/**`
 * belong to concurrent slices this wave — follow-up: lift into
 * `useGoodsReceipts.ts` + `goodsReceipt.api.ts` when those files free up.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  App,
  Alert,
  Badge,
  Button,
  Col,
  DatePicker,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import {
  AccountBookOutlined,
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useAntdTable, useRequest } from 'ahooks';
import PageHeader from '@/components/common/PageHeader';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import { useListPage } from '@/hooks/useListPage';
import type { TableHookResult } from '@/hooks/useListPage';
import { extractApiError } from '@/hooks/useSalesOrders';
import apiClient from '@/services/api/axios.config';
import { API_ROUTES } from '@/constants/api.constants';
import { clearCacheWhere } from '@/utils/cacheKeyRegistry';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type {
  CreateGoodsReceiptRequest,
  GoodsReceipt,
  GoodsReceiptPrefill,
  InvoiceMatchStatus,
} from '@/types/api/goodsReceipt';
import { INVOICE_MATCH_STATUS_ORDER } from '@/types/api/goodsReceipt';
import type { PurchaseOrder } from '@/types/api/purchaseOrder';
import { formatMoney } from '../salesOrders/components/soFormat';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Slice-local data seam (follow-up: lift into hooks/services — see docblock)
// ---------------------------------------------------------------------------

const goodsReceiptApi = {
  list: async (): Promise<{ data: GoodsReceipt[] }> => {
    const { data } = await apiClient.get<{ data: GoodsReceipt[] }>(API_ROUTES.GOODS_RECEIPTS);
    return data;
  },
  prefill: async (poId: string): Promise<{ data: GoodsReceiptPrefill }> => {
    const { data } = await apiClient.get<{ data: GoodsReceiptPrefill }>(
      `${API_ROUTES.GOODS_RECEIPTS}/prefill`,
      { params: { poId } }
    );
    return data;
  },
  create: async (body: CreateGoodsReceiptRequest): Promise<{ data: GoodsReceipt }> => {
    const { data } = await apiClient.post<{ data: GoodsReceipt }>(API_ROUTES.GOODS_RECEIPTS, body);
    return data;
  },
};

/** Invalidate every cache a docket post can stale (receipt/OCC/finance/PO). */
function invalidateGoodsReceiptCaches() {
  clearCacheWhere(
    (key) =>
      key.startsWith('/api/goods-receipts') ||
      key.startsWith('/api/finance/receipt-invoice-match') ||
      key.startsWith('/api/occ') ||
      key.startsWith('/api/purchase-orders')
  );
}

interface GrFilters {
  search: string;
  matchStatus: 'all' | InvoiceMatchStatus;
}

/** House list contract: useAntdTable + pre-first-fetch guard, client filter at mock scale. */
function useGoodsReceiptsTable(filters: GrFilters): TableHookResult<GoodsReceipt> {
  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      const response = await goodsReceiptApi.list();
      const term = filters.search.trim().toLowerCase();
      const rows = response.data.filter((docket) => {
        if (filters.matchStatus !== 'all' && docket.invoiceMatchStatus !== filters.matchStatus) {
          return false;
        }
        if (!term) return true;
        return [
          docket.docketNumber,
          docket.poNumber,
          docket.vendorName,
          ...docket.containerNumbers,
        ]
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
      const start = (current - 1) * pageSize;
      return { total: rows.length, list: rows.slice(start, start + pageSize) };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [filters.search, filters.matchStatus],
    }
  );
  return {
    tableProps,
    isLoading: loading || params.length === 0,
    error: error ?? null,
    refresh,
  };
}

// ---------------------------------------------------------------------------
// Docket form (zodResolver, mode onTouched — house contract)
// ---------------------------------------------------------------------------

const docketSchema = z.object({
  poId: z.string().min(1, 'procurement:receipt.validation.poRequired'),
  docketNumber: z.string().min(1, 'procurement:receipt.validation.docketRequired'),
  receiptDate: z.string().min(1, 'procurement:receipt.validation.dateRequired'),
});

type DocketFormData = z.infer<typeof docketSchema>;

const defaultFormValues: DocketFormData = {
  poId: '',
  docketNumber: '',
  receiptDate: dayjs().format('YYYY-MM-DD'),
};

interface DraftLine {
  poLineId: string;
  sku: string;
  productName: string;
  qtyLoaded: number;
  qtyReceived: number;
  qtyOpenToReceive: number;
  price: number;
  qty: number;
}

/** antd Tag preset per 會計對帳 chip (PO list precedent; no colour literals). */
const MATCH_TAG_COLOR: Record<InvoiceMatchStatus, string> = {
  matched: 'green',
  variance: 'orange',
  unmatched: 'default',
};

interface GrFilterPopoverValues extends FilterValues {
  matchStatus?: string;
}

const GoodsReceiptPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['procurement', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const [filters, setFilters] = useState<GrFilters>({ search: '', matchStatus: 'all' });
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);
  const [linesError, setLinesError] = useState<string | null>(null);

  const customFilterCounters = useMemo(
    () => ({ matchStatus: (value: unknown) => (value === 'all' ? 0 : 1) }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    GrFilters,
    GoodsReceipt
  >({
    useTableHook: useGoodsReceiptsTable,
    filters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  // PO options for the docket modal (mock-scale fetch, shared static cacheKey).
  const { data: poOptions, loading: poOptionsLoading } = useRequest(
    async () => {
      const { data } = await apiClient.get<{ data: PurchaseOrder[] }>(API_ROUTES.PURCHASE_ORDERS, {
        params: { page: 1, pageSize: 200 },
      });
      return data.data;
    },
    { cacheKey: '/api/purchase-orders?view=goods-receipt-options' }
  );

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DocketFormData>({
    resolver: zodResolver(docketSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // A3 actual-at-loading feed — refetches per selected PO (manual run).
  const {
    data: prefill,
    loading: prefillLoading,
    run: loadPrefill,
    mutate: setPrefill,
  } = useRequest(async (poId: string) => (await goodsReceiptApi.prefill(poId)).data, {
    manual: true,
    onSuccess: (feed) => {
      setDraftLines(
        feed.lines.map((line) => ({
          poLineId: line.poLineId,
          sku: line.sku,
          productName: line.productName,
          qtyLoaded: line.qtyLoaded,
          qtyReceived: line.qtyReceived,
          qtyOpenToReceive: line.qtyOpenToReceive,
          price: line.price,
          qty: line.qtyOpenToReceive,
        }))
      );
      setLinesError(null);
    },
  });

  const sealedConfirmed = prefill?.sealedConfirmed ?? false;
  const receivableTotal = draftLines.reduce((sum, line) => sum + line.qtyOpenToReceive, 0);

  const closeModal = () => {
    setModalOpen(false);
    reset(defaultFormValues);
    setPrefill(undefined);
    setDraftLines([]);
    setLinesError(null);
  };

  const localizedPostError = (body: { error?: string; message?: string }): string => {
    if (body.error === 'NOT_SEALED') return t('procurement:receipt.errors.notSealed');
    if (body.error === 'OVER_RECEIPT') return t('procurement:receipt.errors.overReceipt');
    return body.message || t('common:messages.createFailed');
  };

  const onSubmit = async (values: DocketFormData) => {
    const lines = draftLines.filter((line) => line.qty > 0);
    if (lines.length === 0) {
      setLinesError(t('procurement:receipt.validation.atLeastOne'));
      return;
    }
    try {
      await goodsReceiptApi.create({
        poId: values.poId,
        docketNumber: values.docketNumber.trim(),
        receiptDate: values.receiptDate,
        lines: lines.map((line) => ({ poLineId: line.poLineId, qty: line.qty })),
      });
      invalidateGoodsReceiptCaches();
      message.success(t('procurement:receipt.messages.posted'));
      closeModal();
      refresh();
    } catch (error) {
      message.error(localizedPostError(extractApiError(error)));
    }
  };

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'matchStatus',
        label: t('procurement:receipt.fields.matchStatus'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('procurement:receipt.filters.allMatchStatuses') },
          ...INVOICE_MATCH_STATUS_ORDER.map((status) => ({
            value: status,
            label: t(`procurement:receipt.match.${status}`),
          })),
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t]
  );

  const fieldError = (key: keyof DocketFormData) =>
    errors[key] ? <Text style={formStyles.errorStyle}>{t(errors[key]?.message ?? '')}</Text> : null;

  const columns: TableProps<GoodsReceipt>['columns'] = [
    {
      title: t('procurement:receipt.fields.docketNumber'),
      dataIndex: 'docketNumber',
      key: 'docketNumber',
      width: 180,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText text={record.docketNumber} searchTerm={filters.search} fallback="-" />
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {record.receiptDate}
          </span>
        </div>
      ),
    },
    {
      title: t('procurement:receipt.fields.poNumber'),
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 220,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <HighlightText text={record.poNumber} searchTerm={filters.search} fallback="-" />
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            <HighlightText text={record.vendorName} searchTerm={filters.search} fallback="" />
          </span>
        </div>
      ),
    },
    {
      title: t('procurement:receipt.fields.containers'),
      dataIndex: 'containerNumbers',
      key: 'containerNumbers',
      width: 150,
      render: (containers: string[]) =>
        containers.length > 0 ? (
          <div className="tw-flex tw-gap-1 tw-flex-wrap">
            {containers.map((container) => (
              <Tag key={container} style={{ margin: 0 }}>
                {container}
              </Tag>
            ))}
          </div>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('procurement:receipt.fields.qtyTotal'),
      key: 'qtyTotal',
      width: 110,
      align: 'right',
      render: (_: unknown, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {record.lines.reduce((sum, line) => sum + line.qty, 0)}
        </span>
      ),
    },
    {
      title: t('procurement:receipt.fields.amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 150,
      align: 'right',
      render: (_: number, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(record.currency, record.totalAmount)}
        </span>
      ),
    },
    {
      title: t('procurement:receipt.fields.matchStatus'),
      dataIndex: 'invoiceMatchStatus',
      key: 'invoiceMatchStatus',
      width: 130,
      render: (status: InvoiceMatchStatus) => (
        <Tag color={MATCH_TAG_COLOR[status]} style={{ margin: 0 }}>
          {t(`procurement:receipt.match.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('procurement:receipt.fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'viewPo',
                label: t('procurement:receipt.actions.viewPo'),
                icon: <EyeOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  navigate(`/auth/purchase-orders/${record.poId}`);
                },
              },
              {
                key: 'match',
                label: t('procurement:receipt.actions.openMatch'),
                icon: <AccountBookOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  navigate('/auth/finance/receipt-match');
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('procurement:receipt.actions.actionsFor', {
              docketNumber: record.docketNumber,
            })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <PageHeader title={t('procurement:receipt.listTitle')} />

      {/* Search + match filter + 對帳/post toolbar */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('procurement:receipt.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<GrFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={(values) =>
            setFilters((prev) => ({
              ...prev,
              matchStatus: (values.matchStatus as GrFilters['matchStatus']) || 'all',
            }))
          }
          initialFilters={{ matchStatus: filters.matchStatus }}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
        <div className="tw-flex tw-gap-3" style={{ marginLeft: 'auto' }}>
          <Button
            icon={<AccountBookOutlined />}
            size="large"
            onClick={() => navigate('/auth/finance/receipt-match')}
            style={{
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
            }}
          >
            {t('procurement:receipt.openMatchView')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => setModalOpen(true)}
            style={{
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
              paddingLeft: token.paddingLG,
              paddingRight: token.paddingLG,
            }}
          >
            {t('procurement:receipt.addReceipt')}
          </Button>
        </div>
      </div>

      {/* Table — S0.3 state triad via TableContainer + ListEmptyState */}
      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('procurement:receipt.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<GoodsReceipt>
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('procurement:receipt.states.noResults')}
                actionLabel={t('procurement:receipt.states.clearFilters')}
                onAction={() => setFilters({ search: '', matchStatus: 'all' })}
                actionType="default"
              />
            ) : (
              <ListEmptyState
                title={t('procurement:receipt.states.noReceipts')}
                actionLabel={t('procurement:receipt.addReceipt')}
                onAction={() => setModalOpen(true)}
                actionIcon={<PlusOutlined />}
              />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 1020 }}
        />
      </TableContainer>

      {/* 進貨單 docket modal — prefilled from the A3 actual-at-loading feed */}
      <Modal
        open={modalOpen}
        title={t('procurement:receipt.modal.title')}
        onCancel={closeModal}
        width={800}
        footer={[
          <Button
            key="cancel"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={closeModal}
          >
            {t('common:actions.cancel')}
          </Button>,
          // 已封櫃 precondition — hide the post action entirely when unsealed
          // or nothing is receivable (hide-don't-disable).
          ...(prefill && (!sealedConfirmed || receivableTotal === 0)
            ? []
            : [
                <Button
                  key="post"
                  type="primary"
                  size="large"
                  style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                  loading={isSubmitting}
                  onClick={() => void handleSubmit(onSubmit)()}
                >
                  {t('procurement:receipt.modal.submit')}
                </Button>,
              ]),
        ]}
      >
        <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('procurement:receipt.modal.sourceSection')}
          </Text>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('procurement:receipt.fields.poNumber')}
              </Text>
              <Controller
                name="poId"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value || undefined}
                    onChange={(value) => {
                      field.onChange(value ?? '');
                      trigger('poId');
                      if (value) loadPrefill(value);
                    }}
                    onBlur={field.onBlur}
                    options={(poOptions ?? []).map((po) => ({
                      value: po.id,
                      label: `${po.poNumber} — ${po.vendorName}`,
                    }))}
                    loading={poOptionsLoading}
                    showSearch
                    optionFilterProp="label"
                    placeholder={t('procurement:receipt.modal.selectPoPlaceholder')}
                    size="large"
                    style={formStyles.inputStyle}
                    status={errors.poId ? 'error' : undefined}
                  />
                )}
              />
              {fieldError('poId')}
            </Col>
          </Row>
          {prefill && sealedConfirmed && (
            <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
              <Tag color="green" style={{ margin: 0 }}>
                {t('procurement:receipt.fields.sealed')}
              </Tag>
              {prefill.containerNumbers.map((container) => (
                <Tag key={container} style={{ margin: 0 }}>
                  {t('procurement:receipt.fields.containerTag', { container })}
                </Tag>
              ))}
            </div>
          )}
          {prefill && !sealedConfirmed && (
            <Alert
              className="tw-mt-3"
              type="warning"
              showIcon
              message={t('procurement:receipt.modal.notSealedTitle')}
              description={t('procurement:receipt.modal.notSealedHint')}
            />
          )}
          {prefill && sealedConfirmed && receivableTotal === 0 && (
            <Alert
              className="tw-mt-3"
              type="info"
              showIcon
              message={t('procurement:receipt.modal.nothingOpenTitle')}
              description={t('procurement:receipt.modal.nothingOpenHint')}
            />
          )}
        </div>

        {prefill && sealedConfirmed && receivableTotal > 0 && (
          <>
            <div
              style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}
            >
              <Text strong style={formStyles.sectionTitleStyle}>
                {t('procurement:receipt.modal.docketSection')}
              </Text>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                    {t('procurement:receipt.fields.docketNumber')}
                  </Text>
                  <Controller
                    name="docketNumber"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder={t('procurement:receipt.modal.docketPlaceholder')}
                        size="large"
                        style={formStyles.inputStyle}
                        status={errors.docketNumber ? 'error' : undefined}
                      />
                    )}
                  />
                  {fieldError('docketNumber')}
                </Col>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                    {t('procurement:receipt.fields.receiptDate')}
                  </Text>
                  <Controller
                    name="receiptDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(value) => {
                          field.onChange(value ? value.format('YYYY-MM-DD') : '');
                          trigger('receiptDate');
                        }}
                        onBlur={field.onBlur}
                        size="large"
                        style={formStyles.inputStyle}
                        status={errors.receiptDate ? 'error' : undefined}
                      />
                    )}
                  />
                  {fieldError('receiptDate')}
                </Col>
              </Row>
            </div>

            <div style={{ ...formStyles.fieldContainerStyle, padding: '20px' }}>
              <Text strong style={formStyles.sectionTitleStyle}>
                {t('procurement:receipt.modal.linesSection')}
              </Text>
              <Text
                type="secondary"
                style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: '12px' }}
              >
                {t('procurement:receipt.modal.a3Hint')}
              </Text>
              <Table<DraftLine>
                size="small"
                rowKey="poLineId"
                loading={prefillLoading}
                dataSource={draftLines}
                pagination={false}
                columns={[
                  {
                    title: t('procurement:receipt.fields.product'),
                    key: 'product',
                    render: (_: unknown, line) => (
                      <div className="tw-flex tw-flex-col tw-gap-0.5">
                        <span style={{ fontWeight: token.fontWeightStrong }}>{line.sku}</span>
                        <span
                          style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
                        >
                          {line.productName}
                        </span>
                      </div>
                    ),
                  },
                  {
                    title: t('procurement:receipt.fields.qtyLoaded'),
                    dataIndex: 'qtyLoaded',
                    key: 'qtyLoaded',
                    align: 'right',
                    render: (value: number) => (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                    ),
                  },
                  {
                    title: t('procurement:receipt.fields.qtyReceivedSoFar'),
                    dataIndex: 'qtyReceived',
                    key: 'qtyReceived',
                    align: 'right',
                    render: (value: number) => (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                    ),
                  },
                  {
                    title: t('procurement:receipt.fields.qtyOpenToReceive'),
                    dataIndex: 'qtyOpenToReceive',
                    key: 'qtyOpenToReceive',
                    align: 'right',
                    render: (value: number) => (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                    ),
                  },
                  {
                    title: t('procurement:receipt.fields.qtyToReceive'),
                    key: 'qty',
                    width: 140,
                    render: (_: unknown, line) => (
                      <InputNumber
                        min={0}
                        max={line.qtyOpenToReceive}
                        value={line.qty}
                        size="large"
                        style={{ width: '100%' }}
                        aria-label={t('procurement:receipt.fields.qtyToReceiveFor', {
                          sku: line.sku,
                        })}
                        onChange={(value) => {
                          setLinesError(null);
                          setDraftLines((prev) =>
                            prev.map((candidate) =>
                              candidate.poLineId === line.poLineId
                                ? { ...candidate, qty: value ?? 0 }
                                : candidate
                            )
                          );
                        }}
                      />
                    ),
                  },
                  {
                    title: t('procurement:receipt.fields.price'),
                    dataIndex: 'price',
                    key: 'price',
                    align: 'right',
                    render: (value: number) => (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(prefill.currency, value)}
                      </span>
                    ),
                  },
                ]}
              />
              {linesError && <Text style={formStyles.errorStyle}>{linesError}</Text>}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default GoodsReceiptPage;
